import React from "react";
import { Dialog, DialogContent, DialogActions, Button, Typography, Box, LinearProgress, CircularProgress, IconButton } from "@mui/material";
import { StorageOutlined, Close as CloseIcon } from "@mui/icons-material";
import PropTypes from "prop-types";

export const PHASE_LABELS = {
    IDLE: "In attesa...",
    INDEXING: "Indicizzazione in corso...",
    PURGING: "Indicizzazione completata. Pulizia orfani...",
    DONE: "Indicizzazione completata",
    FAILED: "Errore durante l'indicizzazione"
};

export const PHASE_DETAILS = {
    PURGING: "I documenti indicizzati sono gia' ricercabili. La pulizia rimuove solo gli orfani rimasti nell'indice."
};

// Estrae i valori normalizzati dallo stato del reindex
function readStatus(status) {
    const phase = status?.phase || "IDLE";
    return {
        phase,
        running: Boolean(status?.running),
        percentage: Math.min(100, Math.max(0, status?.percentage ?? 0)),
        processed: status?.processed ?? 0,
        total: status?.total ?? 0,
        isError: phase === "FAILED"
    };
}

// Widget circolare ridotto, mostrato in basso a destra quando il dialog è minimizzato
const MinimizedIndexProgress = ({ status, onExpand, onDismiss }) => {
    const { phase, percentage, processed, total, isError } = readStatus(status);

    return (
        <Box
            onClick={onExpand}
            sx={{
                position: "fixed",
                bottom: 24,
                right: 24,
                zIndex: 1400,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                backgroundColor: "white",
                borderRadius: "20px",
                boxShadow: "0px 6px 20px rgba(0, 0, 0, 0.25)",
                padding: "10px 16px 10px 10px",
                cursor: "pointer",
                "&:hover": { boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.32)" }
            }}
        >
            <Box sx={{ position: "relative", display: "inline-flex" }}>
                <CircularProgress
                    variant="determinate"
                    value={percentage}
                    size={48}
                    thickness={4}
                    sx={{ color: isError ? "#e00036" : "#0b3d87" }}
                />
                <Box sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    bottom: 0,
                    right: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                }}>
                    <Typography sx={{ fontFamily: "Rubik", fontSize: "11px", fontWeight: "bold", color: "#0b3d87" }}>
                        {percentage}%
                    </Typography>
                </Box>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography sx={{ fontFamily: "Rubik", fontSize: "12px", fontWeight: "bold", color: "#0b3d87" }}>
                    {PHASE_LABELS[phase] || PHASE_LABELS.IDLE}
                </Typography>
                <Typography sx={{ fontFamily: "Rubik", fontSize: "12px", color: "#0b3d87" }}>
                    {processed} / {total} documenti
                </Typography>
            </Box>

            <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); onDismiss(); }}
                sx={{ color: "#0b3d87", ml: 0.5 }}
            >
                <CloseIcon fontSize="small" />
            </IconButton>
        </Box>
    );
};

MinimizedIndexProgress.propTypes = {
    status: PropTypes.object,
    onExpand: PropTypes.func.isRequired,
    onDismiss: PropTypes.func.isRequired
};

const IndexProgressDialog = ({ open, minimized, status, onClose, onExpand, onDismiss }) => {
    if (minimized) {
        return <MinimizedIndexProgress status={status} onExpand={onExpand} onDismiss={onDismiss} />;
    }

    const { phase, percentage, processed, total, isError } = readStatus(status);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { borderRadius: "28px" } }}
        >
            <DialogContent sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
                padding: "30px"
            }}>
                <Box sx={{
                    width: 80,
                    height: 80,
                    backgroundColor: isError ? "#e00036" : "#0b3d87",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "50%",
                    marginBottom: 2
                }}>
                    <StorageOutlined sx={{ color: "white", fontSize: 45 }} />
                </Box>

                <Typography variant="h5" sx={{ fontFamily: "Rubik", fontWeight: "bold", color: "#0b3d87", marginBottom: 2 }}>
                    {PHASE_LABELS[phase] || PHASE_LABELS.IDLE}
                </Typography>

                <Box sx={{ width: "100%", maxWidth: 420, marginBottom: 1 }}>
                    <LinearProgress
                        variant="determinate"
                        value={percentage}
                        color={isError ? "error" : "primary"}
                        sx={{ height: 12, borderRadius: 6 }}
                    />
                </Box>

                <Typography sx={{ fontFamily: "Rubik", fontSize: "14px", color: "#0b3d87", marginBottom: PHASE_DETAILS[phase] ? 1 : 2 }}>
                    {percentage}% — {processed} / {total} documenti
                </Typography>

                {PHASE_DETAILS[phase] ? (
                    <Typography sx={{ fontFamily: "Rubik", fontSize: "13px", color: "#0b3d87", marginBottom: 2, maxWidth: 420 }}>
                        {PHASE_DETAILS[phase]}
                    </Typography>
                ) : null}

                <DialogActions sx={{ width: "100%", justifyContent: "center" }}>
                    <Button
                        onClick={onClose}
                        sx={{
                            fontFamily: "Rubik",
                            backgroundColor: "#0b3d87",
                            color: "white",
                            fontWeight: "bold",
                            padding: "8px 20px",
                            borderRadius: "20px",
                            "&:hover": { backgroundColor: "#092b6a" }
                        }}
                    >
                        CHIUDI
                    </Button>
                </DialogActions>
            </DialogContent>
        </Dialog>
    );
};

IndexProgressDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    minimized: PropTypes.bool,
    status: PropTypes.shape({
        running: PropTypes.bool,
        phase: PropTypes.string,
        percentage: PropTypes.number,
        processed: PropTypes.number,
        total: PropTypes.number
    }),
    onClose: PropTypes.func.isRequired,
    onExpand: PropTypes.func.isRequired,
    onDismiss: PropTypes.func.isRequired
};

export default IndexProgressDialog;
